import React, { useContext } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-resource-router';

import WarningIcon from '@atlaskit/icon/glyph/warning';
import Lozenge from '@atlaskit/lozenge';
import { Inline } from '@atlaskit/primitives';
import Tabs, { TabList, useTab } from '@atlaskit/tabs';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { useIntl } from 'src/hooks/intl';
import { getConflictStatus } from 'src/redux/pull-request/selectors';
import { useParams } from 'src/router/hooks';
import urls from 'src/urls';

import { TabsContext } from './context/tabs-context';
import { DiffStateContext, SelectedDiffType } from './diff/diff-state-context';
import { messages } from './pull-request-tabbed.i18n';

const StyledLink = styled(Link)({
  '&:hover': {
    textDecoration: 'none',
  },
});

const LinkTab = ({
  children,
  href,
}: {
  children: any;
  href: string;
}): JSX.Element => {
  const {
    onKeyDown,
    tabIndex,
    onClick: onTabClick,
    ...tabAttributes
  } = useTab();
  return (
    <StyledLink
      to={href}
      replace
      {...tabAttributes}
      onClick={event => {
        // we want to prevent default behavior for metaKey clicks
        // if we don't have this here the tab will change when trying to
        // cmd+click or shift+click.
        // We want to keep to have those open in a new tab/window
        // but keep the current tab unchanged
        if (!event.metaKey && !event.shiftKey) {
          onTabClick();
        }
      }}
    >
      {children}
    </StyledLink>
  );
};

function TabWithCount({
  text,
  href,
  count,
  icon,
}: {
  text: string | JSX.Element;
  href: string;
  count?: number | string;
  icon?: JSX.Element;
}): JSX.Element {
  return (
    <LinkTab href={href}>
      <Inline space="space.050" alignBlock="center">
        {text}
        {!!count && <Lozenge>{count}</Lozenge>}
        {icon}
      </Inline>
    </LinkTab>
  );
}

type Props = {
  isLoadingConflicts: boolean;
  // Optional: pass TabPanel as props.children to show different tab content.
  // if not passed it will just render the tab list to be able to switch tabs
  children?: any;
};

/**
 * PRTabsContainer uses @atlaskit/tabs to render the parent Tabs component with
 * nested TabList and 3 Tab elements (Overview, Files changed, Commits).
 *
 * It uses the TabsContext to get the selected tab index, tab change handler,
 * and counts for files and commits.
 */
export const PRTabsContainer = ({ isLoadingConflicts, children }: Props) => {
  const intl = useIntl();
  const { selectedTabIndex, onSelectTab, fileCount, commitCount } =
    useContext(TabsContext);
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();

  const hasConflicts = useSelector(getConflictStatus);

  const tabLink = (tab: 'overview' | 'commits' | 'diff') =>
    urls.ui.pullRequestTab(repositoryOwner, repositorySlug, pullRequestId, tab);

  const { iterativeReviewSelectedDiff } = useContext(DiffStateContext);

  const diffTabLink = () => {
    if (
      iterativeReviewSelectedDiff.sourceHash &&
      iterativeReviewSelectedDiff.destHash &&
      iterativeReviewSelectedDiff.selectedDiffType !== SelectedDiffType.All
    )
      return `${tabLink('diff')}/${iterativeReviewSelectedDiff.sourceHash}..${
        iterativeReviewSelectedDiff.destHash
      }`;
    return tabLink('diff');
  };

  const diffTabWarningIcon = () => {
    if (!isLoadingConflicts && hasConflicts)
      return (
        <WarningIcon
          label="warning"
          primaryColor={token('color.icon.warning', colors.Y400)}
          testId="diff-conflict-warning-icon"
        />
      );
    return undefined;
  };

  return (
    <Tabs
      id="pull-request-tabs"
      selected={selectedTabIndex}
      onChange={onSelectTab}
      shouldUnmountTabPanelOnChange
    >
      <nav aria-label={intl.formatMessage(messages.tabsListLabel)}>
        <TabList>
          <LinkTab href={tabLink('overview')}>
            <FormattedMessage {...messages.overviewTabTitle} />
          </LinkTab>
          <TabWithCount
            text={<FormattedMessage {...messages.diffTabTitle} />}
            href={diffTabLink()}
            count={fileCount}
            icon={diffTabWarningIcon()}
          />
          <TabWithCount
            text={<FormattedMessage {...messages.commitsTabTitle} />}
            href={tabLink('commits')}
            count={commitCount}
          />
        </TabList>
      </nav>
      {children || null}
    </Tabs>
  );
};
